import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
  InputAdornment,
  Switch,
  CssBaseline,
  Menu,
  MenuItem,
} from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import api from '../../../api/axios';
import CentroDialog from '../../../components/CentroDialog';
import RelationDialog from '../../../components/RelationDialog';
import { useCentros, Centro } from '../../../hooks/useCentros';
import theme from '../../../styles/theme';
import moralejaImg from '../../../imagenes/Centros/moraleja.png';
import pozueloImg from '../../../imagenes/Centros/pozuelo.png';
import plazacastillaImg from '../../../imagenes/Centros/plazacastilla.png';
import otherImg from '../../../imagenes/Centros/other.png';

import { useAuth } from '../../../context/AuthContext';
import AutoAssignDialog from '../../../components/aulas/AutoAssignDialog';

type RelItem = { id: number; nombre: string };

export default function CentrosPage() {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <CentrosPageContent />
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

function CentrosPageContent() {
  const { t } = useTranslation<'translation'>();
  const { rows, loading, refresh, create, update, remove, toggle } = useCentros();

  /* auth */
  const { user } = useAuth();
  const isAdmin = Array.isArray(user?.roles) && user!.roles.includes('admin');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Centro | undefined>();
  const [search, setSearch] = useState('');

  const emptyLoader = () => Promise.resolve([] as RelItem[]);
  const [relOpen, setRelOpen] = useState(false);
  const [relTitle, setRelTitle] = useState('');
  const [relLoader, setRelLoader] = useState<() => Promise<RelItem[]>>(emptyLoader);

  const [toolsAnchor, setToolsAnchor] = useState<null | HTMLElement>(null);
  const toolsOpen = Boolean(toolsAnchor);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCentroId, setBulkCentroId] = useState<number | undefined>(undefined);

  /* React Query carga automáticamente, no necesitamos refresh manual */

  const openNew = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };
  const openEdit = (c: Centro) => {
    setEditing(c);
    setDialogOpen(true);
  };

  const openRels = useCallback(
    (c: Centro) => {
      setRelTitle(t<string>('centers.relDialog.title', { name: c.nombre }));
      const loader = () => api.get(`/centros/${c.id}/profesores/`).then((r) => r.data as RelItem[]);
      setRelLoader(() => loader);
      setRelOpen(true);
    },
    [t],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((c) => (c.nombre ?? '').toLowerCase().includes(q));
  }, [rows, search]);

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button startIcon={<AddIcon />} variant="contained" onClick={openNew}>
          {t('centers.button.new') ?? 'NUEVO CENTRO'}
        </Button>

        <TextField
          placeholder={t<string>('centers.search.placeholder') ?? 'Buscar centro…'}
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {isAdmin && (
          <>
            <Tooltip title="Herramientas admin">
              <IconButton
                size="small"
                onClick={(e) => setToolsAnchor(e.currentTarget)}
                sx={{
                  ml: 1,
                  opacity: 0.6,
                  '&:hover': { opacity: 1 },
                }}
                aria-label="Herramientas admin"
              >
                <MoreHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={toolsAnchor}
              open={toolsOpen}
              onClose={() => setToolsAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  setToolsAnchor(null);
                  setBulkCentroId(undefined);
                  setBulkOpen(true);
                }}
              >
                Auto-asignar aulas (bulk)
              </MenuItem>
            </Menu>
          </>
        )}
      </Stack>

      <Paper>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Icono</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>{t('centers.table.name') ?? 'Nombre'}</TableCell>
                <TableCell align="center">{t('centers.table.active') ?? 'Activo'}</TableCell>
                <TableCell width={200}></TableCell>
                <TableCell width={150} align="right"></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <img
                      src={
                        c.nombre === 'Moraleja'
                          ? moralejaImg
                          : c.nombre === 'Pozuelo'
                            ? pozueloImg
                            : c.nombre === 'Chamartín'
                              ? plazacastillaImg
                              : otherImg
                      }
                      alt={c.nombre}
                      style={{ height: 32, width: 'auto' }}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src = otherImg;
                      }}
                    />
                  </TableCell>

                  <TableCell>{c.id}</TableCell>
                  <TableCell>{c.nombre}</TableCell>
                  <TableCell align="center">
                    <Switch checked={c.activo} onChange={(_, v) => toggle(c.id, v)} />
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={{ minWidth: 80 }}
                        component={RouterLink as any}
                        to={`/admin/manager/aulas/${c.id}`}
                      >
                        Aulas
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={{ minWidth: 80 }}
                        component={RouterLink as any}
                        to={`/admin/manager/aulas/${c.id}/planificador`}
                      >
                        Planner
                      </Button>

                      {isAdmin && (
                        <Tooltip title="Auto-asignar (solo este centro)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setBulkCentroId(c.id);
                              setBulkOpen(true);
                            }}
                            sx={{ ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}
                            aria-label="Auto-asignar aulas en este centro"
                          >
                            <AutoFixHighIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>

                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title={t('centers.tooltip.professors') ?? 'Profesores'}>
                        <IconButton size="small" onClick={() => openRels(c)}>
                          <PeopleAltIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={t('common.actions.edit') ?? 'Editar'}>
                        <IconButton size="small" onClick={() => openEdit(c)}>
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={t('common.actions.delete') ?? 'Eliminar'}>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            if (window.confirm(t('centers.deleteConfirm') ?? '¿Eliminar centro?')) {
                              await remove(c.id);
                            }
                          }}
                          sx={{ color: 'crimson.main' }}
                        >
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}

              {!filtered.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t('centers.table.noResults') ?? 'Sin resultados'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <CentroDialog
        open={dialogOpen}
        initial={editing}
        onClose={() => setDialogOpen(false)}
        onSave={async (data) => {
          editing ? await update(editing.id, data) : await create(data);
          setDialogOpen(false);
        }}
        onDelete={
          editing
            ? async () => {
                if (window.confirm(t('centers.deleteConfirm') ?? '¿Eliminar centro?')) {
                  await remove(editing.id);
                  setDialogOpen(false);
                }
              }
            : undefined
        }
      />

      <RelationDialog
        open={relOpen}
        title={relTitle}
        loader={relLoader}
        onClose={() => setRelOpen(false)}
      />

      {isAdmin && (
        <AutoAssignDialog
          open={bulkOpen}
          onClose={() => {
            setBulkOpen(false);
            setBulkCentroId(undefined);
          }}
          defaultCentroId={bulkCentroId}
        />
      )}
    </Stack>
  );
}
